import { useEffect, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/services/api';
import { getAdminSocket } from '@/services/socket';

function matchesFilters(item, filters) {
  if (filters.category && item.category !== filters.category) return false;
  if (filters.severity && item.severity !== filters.severity) return false;
  if (filters.status && item.status !== filters.status) return false;
  if (filters.unread && item.readAt) return false;
  const query = filters.q?.trim().toLowerCase();
  if (query && !`${item.title ?? ''} ${item.body ?? ''} ${item.type ?? ''}`.toLowerCase().includes(query)) return false;
  return true;
}

function upsert(items, next) {
  if (!next?._id) return items;
  return [next, ...items.filter((item) => item._id !== next._id)]
    .sort((first, second) => new Date(second.createdAt) - new Date(first.createdAt));
}

export function useAdminNotifications(filters) {
  const queryClient = useQueryClient();
  const queryKey = useMemo(() => ['admin-notifications', filters], [filters]);
  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const params = new URLSearchParams({ limit: '200' });
      for (const [key, value] of Object.entries(filters)) {
        if (value) params.set(key, String(value));
      }
      const result = await api(`/api/v1/admin/notifications?${params}`);
      return {
        items: result.data ?? [],
        summary: result.summary ?? {},
        pagination: result.pagination ?? {},
      };
    },
  });

  useEffect(() => {
    const socket = getAdminSocket();
    const onCreated = (envelope) => {
      const item = envelope?.data;
      if (!item || !matchesFilters(item, filters)) return;
      queryClient.setQueryData(queryKey, (current) => {
        const existing = current?.items?.find((row) => row._id === item._id);
        return {
          ...(current ?? { items: [], summary: {} }),
          items: upsert(current?.items ?? [], item),
          summary: existing ? current?.summary ?? {} : {
            ...(current?.summary ?? {}),
            total: Number(current?.summary?.total ?? 0) + 1,
            unread: Number(current?.summary?.unread ?? 0) + (item.readAt ? 0 : 1),
            open: Number(current?.summary?.open ?? 0) + (item.status === 'open' ? 1 : 0),
            emergency: Number(current?.summary?.emergency ?? 0) + (item.severity === 'emergency' ? 1 : 0),
          },
        };
      });
      void queryClient.invalidateQueries({ queryKey });
    };
    const onUpdated = (envelope) => {
      const item = envelope?.data;
      if (!item) return;
      queryClient.setQueryData(queryKey, (current) => {
        if (!current) return current;
        const items = current.items.filter((row) => row._id !== item._id);
        return { ...current, items: matchesFilters(item, filters) ? upsert(items, item) : items };
      });
      void queryClient.invalidateQueries({ queryKey });
    };
    const resync = () => queryClient.invalidateQueries({ queryKey });
    const onReadAll = () => queryClient.setQueryData(queryKey, (current) => current ? {
      ...current,
      items: current.items.map((item) => ({ ...item, readAt: item.readAt ?? new Date().toISOString() })),
      summary: { ...current.summary, unread: 0 },
    } : current);
    socket.on('notification:created', onCreated);
    socket.on('notification:updated', onUpdated);
    socket.on('notification:bulk_updated', resync);
    socket.on('notification:read_all', onReadAll);
    socket.on('connect', resync);
    socket.on('connection:ready', resync);
    return () => {
      socket.off('notification:created', onCreated);
      socket.off('notification:updated', onUpdated);
      socket.off('notification:bulk_updated', resync);
      socket.off('notification:read_all', onReadAll);
      socket.off('connect', resync);
      socket.off('connection:ready', resync);
    };
  }, [filters, queryClient, queryKey]);

  return query;
}
